import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createScheduleMaster, updateScheduleMaster, getScheduleMasterById } from '../services/api';
import { toast } from 'react-toastify';
import './MasterForm.css';

const ScheduleMasterForm = () => {
  const navigate = useNavigate();
  const { scheduleId } = useParams();
  const isEdit = Boolean(scheduleId);
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    frequency: 'daily',
    time: '02:00',
    daysOfWeek: [],
    dayOfMonth: 1,
    timezone: 'UTC',
    isActive: true
  });

  useEffect(() => {
    if (isEdit) {
      fetchSchedule();
    }
  }, [scheduleId]);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      const response = await getScheduleMasterById(scheduleId);
      setFormData(response.data);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch schedule');
      navigate('/schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDaysChange = (day) => {
    setFormData(prev => {
      const days = prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day];
      return { ...prev, daysOfWeek: days.sort() };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await updateScheduleMaster(scheduleId, formData);
        toast.success('Schedule updated successfully');
      } else {
        await createScheduleMaster(formData);
        toast.success('Schedule created successfully');
      }
      navigate('/schedules');
    } catch (error) {
      toast.error(error.message || 'Failed to save schedule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="master-form-container">
      <div className="form-header">
        <h1>{isEdit ? 'Edit Schedule' : 'Create Schedule'}</h1>
        <p>Configure a reusable backup schedule</p>
      </div>

      <form onSubmit={handleSubmit} className="master-form">
        <div className="form-section">
          <h3>Basic Information</h3>
          
          <div className="form-group">
            <label htmlFor="name">Schedule Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Daily 2AM Backup"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Optional description"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Schedule Configuration</h3>
          
          <div className="form-group">
            <label htmlFor="frequency">Frequency *</label>
            <select
              id="frequency"
              name="frequency"
              value={formData.frequency}
              onChange={handleChange}
              required
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="time">Time (HH:MM) *</label>
            <input
              type="time"
              id="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              required
            />
          </div>

          {formData.frequency === 'weekly' && (
            <div className="form-group">
              <label>Days of Week</label>
              <div className="checkbox-group">
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                  <label key={index} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.daysOfWeek.includes(index)}
                      onChange={() => handleDaysChange(index)}
                    />
                    {day}
                  </label>
                ))}
              </div>
            </div>
          )}

          {formData.frequency === 'monthly' && (
            <div className="form-group">
              <label htmlFor="dayOfMonth">Day of Month *</label>
              <input
                type="number"
                id="dayOfMonth"
                name="dayOfMonth"
                value={formData.dayOfMonth}
                onChange={handleChange}
                min="1"
                max="31"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="timezone">Timezone *</label>
            <input
              type="text"
              id="timezone"
              name="timezone"
              value={formData.timezone}
              onChange={handleChange}
              required
              placeholder="e.g., UTC, Asia/Kolkata"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
              Active
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/schedules')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : isEdit ? 'Update Schedule' : 'Create Schedule'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ScheduleMasterForm;